import React, { useCallback, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Col, Form, Input, Label, Row } from "reactstrap"
import Select from "react-select"
import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import moment from "moment"
import { getProviderIpsStock } from "store/provider-dashboard/provider-dashboard-action"
import getDashboardSelectProps from "utils/dashboardFilterStyles"

const FILTER_DEBOUNCE_MS = 400

const ProviderIPsStockFilter = ({
  onCategoryChange,
  provIpsStockLoading,
  getSearchData,
  onProviderIPsAdvancedSearchHandler,
}) => {
  // Retrieve provider Ips data from Redux store
  const providerIps = useSelector(state => state.providerDashboard.ProviderIps)

  // Retrieve provider items data from Redux store
  const ProviderItems = useSelector(
    state => state.providerDashboard.ProviderItems
  )

  // Retrieve projects data from Redux store
  const projects = useSelector(
    state => state.providerDashboard.providerProjects
  )
  // Retrieve provider items categories from Redux store
  const ProviderItemCategoires = useSelector(
    state => state.providerDashboard.ProviderItemCategoires
  )

  // get filtered items based select box props needs
  const filteredItems = useMemo(
    () =>
      ProviderItems?.data.map(item => ({
        value: item.id,
        label: `${item.unfp_item_name} ( ${item.item_name} )`,
      })),
    [ProviderItems]
  )

  const dispatch = useDispatch()

  const fromdateFlatpicker = useRef(null)
  const todateFlatpicker = useRef(null)
  const dispatchTimerRef = useRef(null)
  const [Ips, setIps] = useState(null)
  const [Categories, setCategories] = useState(null)
  const [item, setItem] = useState(null)
  const [batchNumber, setBatchNumber] = useState("")
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [project, setProject] = useState(null)

  const buildSearchParams = useCallback((overrides = {}) => {
    const searchs = {}
    searchs.ips = overrides.ips !== undefined ? overrides.ips : (Ips ? Ips.value : null)
    searchs.category = overrides.category !== undefined ? overrides.category : (Categories ? Categories.value : null)
    searchs.project = overrides.project !== undefined ? overrides.project : (project ? project.value : null)
    searchs.item = overrides.item !== undefined ? overrides.item : (item ? item.map(ite => (ite.value ? ite.value : ite)) : undefined)
    searchs.batchNumber = overrides.batchNumber !== undefined ? overrides.batchNumber : batchNumber
    searchs.from = overrides.from !== undefined ? overrides.from : fromDate
    searchs.to = overrides.to !== undefined ? overrides.to : toDate
    return searchs
  }, [Ips, Categories, project, item, batchNumber, fromDate, toDate])

  const triggerSearch = useCallback((searchParams, displayState) => {
    onProviderIPsAdvancedSearchHandler(searchParams)
    if (displayState) {
      getSearchData(displayState)
    }
    dispatch(getProviderIpsStock(searchParams))
  }, [dispatch, onProviderIPsAdvancedSearchHandler, getSearchData])

  const debouncedSearch = useCallback((overrides = {}, displayState) => {
    clearTimeout(dispatchTimerRef.current)
    dispatchTimerRef.current = setTimeout(() => {
      const params = buildSearchParams(overrides)
      triggerSearch(params, displayState)
    }, FILTER_DEBOUNCE_MS)
  }, [buildSearchParams, triggerSearch])

  // Store filtered data and dispatch the action using onAdvancedSearchHandler callback function
  const handleSubmit = () => {
    clearTimeout(dispatchTimerRef.current)
    const searchs = buildSearchParams()
    triggerSearch(searchs, { Ips, Categories, item, batchNumber, fromDate, toDate })
  }

  // store ip value
  const IpsChangeHandler = ip => {
    setIps(ip)
    debouncedSearch({ ips: ip ? ip.value : null }, { Ips: ip, Categories, item, batchNumber, fromDate, toDate })
  }

  // store Item Categories and fetch items based item category
  const onCategoryChangeHandler = category => {
    onCategoryChange(category ? category.value : null)
    setCategories(category)
    debouncedSearch({ category: category ? category.value : null }, { Ips, Categories: category, item, batchNumber, fromDate, toDate })
  }

  // store multiple items based selected
  const onItemChangeHandler = newItem => {
    const check = newItem.some(ite => ite.value === "all")
    let resolved
    if (check) {
      resolved = filteredItems
    } else {
      resolved = newItem
    }
    setItem(resolved)
    debouncedSearch({ item: resolved ? resolved.map(ite => (ite.value ? ite.value : ite)) : undefined }, { Ips, Categories, item: resolved, batchNumber, fromDate, toDate })
  }

  // store project value
  const onProjectChangeHandler = proj => {
    setProject(proj)
    debouncedSearch({ project: proj ? proj.value : null }, { Ips, Categories, item, batchNumber, fromDate, toDate, project: proj })
  }

  // clear search when clear button clicked
  const onClearHandler = () => {
    clearTimeout(dispatchTimerRef.current)
    fromdateFlatpicker.current.flatpickr.clear()
    todateFlatpicker.current.flatpickr.clear()
    setIps(null)
    setCategories(null)
    setItem(null)
    setBatchNumber("")
    setFromDate(null)
    setToDate(null)
    setProject(null)
    getSearchData({})
    dispatch(getProviderIpsStock())
  }

  return (
    <Form
      onSubmit={e => {
        e.preventDefault()
        handleSubmit()
        return false
      }}
    >
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Projects</Label>
            {projects?.data && (
              <Select
                options={[{ value: "all", label: "All" }, ...projects?.data]}
                value={project}
                onChange={onProjectChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
          </Col>
          <Col>
            {" "}
            <Label className="form-label">IPs</Label>
            {providerIps?.data && (
              <Select
                options={[{ value: "all", label: "All" }, ...providerIps?.data]}
                value={Ips}
                onChange={IpsChangeHandler}
                {...getDashboardSelectProps()}
              />
            )}
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Label className="form-label">Item Categories</Label>
        {ProviderItemCategoires?.data && (
          <Select
            placeholder="All Categories"
            options={[{ value: "all", label: "All" }, ...ProviderItemCategoires?.data]}
            value={Categories}
            onChange={onCategoryChangeHandler}
            {...getDashboardSelectProps()}
          />
        )}
      </div>

      {filteredItems && (
        <div className="mb-3">
          <Label className="form-label">Items</Label>
          <Select
            isMulti={true}
            options={[{ value: "all", label: "All" }, ...filteredItems]}
            value={item}
            onChange={onItemChangeHandler}
            {...getDashboardSelectProps()}
          />
        </div>
      )}
      <div className="mb-3">
        <Label className="form-label">Batch Number</Label>
        <Input
          name="name"
          value={batchNumber}
          onChange={value => {
            const val = value.target.value
            setBatchNumber(val)
            debouncedSearch({ batchNumber: val }, { Ips, Categories, item, batchNumber: val, fromDate, toDate })
          }}
          placeholder="Name"
          type="text"
        />
      </div>

      <div className="mb-3 ">
        <Label className="form-label">From Date</Label>
        <Flatpickr
          ref={fromdateFlatpicker}
          className="form-control d-block"
          placeholder="dd M,yyyy"
          options={{
            altInput: true,
            altFormat: "Y-m-d",
            dateFormat: "Y-m-d",
          }}
          onChange={value => {
            const val = moment(value[0]).format("YYYY-MM-DD")
            setFromDate(val)
            debouncedSearch({ from: val }, { Ips, Categories, item, batchNumber, fromDate: val, toDate })
          }}
        />
        <br />
        <Label className="form-label">To Date</Label>
        <Flatpickr
          ref={todateFlatpicker}
          className="form-control d-block"
          placeholder="dd M,yyyy"
          options={{
            altInput: true,
            altFormat: "Y-m-d",
            dateFormat: "Y-m-d",
          }}
          onChange={value => {
            const val = moment(value[0]).format("YYYY-MM-DD")
            setToDate(val)
            debouncedSearch({ to: val }, { Ips, Categories, item, batchNumber, fromDate, toDate: val })
          }}
        />
      </div>

      <div className="d-flex flex-wrap gap-2">
        <Button color="primary" type="submit" disabled={provIpsStockLoading}>
          {provIpsStockLoading ? "Please Wait..." : "Apply Filters"}
        </Button>
        <Button color="danger" type="button" outline onClick={onClearHandler}>
          Reset All
        </Button>
      </div>
    </Form>
  )
}

export default ProviderIPsStockFilter
